import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  Label,
  Button,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useCallback, useState } from "react"
import {
  useCreateVehicleStockPublish,
  useEditVehicleStockPublish,
  VEHICLE_STOCK_PUBLISH_QUERY_KEY,
} from "hooks/useVehicleStockPublishes"
import { VEHICLE_STOCK_QUERY_KEY } from "hooks/stockHook"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { toast } from "react-toastify"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useQueryClient } from "@tanstack/react-query"
import ButtonCheckboxList from "components/Common/ButtonCheckboxList"

const VehicleStockContentAddEditModal = ({
  currentContent = {},
  data = {},
  isLabel = true,
  buttonLabel = "",
  loading = false,
  onSuccess,
}) => {
  const [modal, setModal] = useState(false)
  const queryClient = useQueryClient()

  const isEdit = !!data?.id

  // Determine button type and labels based on buttonLabel prop
  const buttonType = buttonLabel ? "add" : isEdit ? "edit" : "add"
  const buttonName = buttonLabel || (isEdit ? "Edit Content" : "Add Content")
  const modalTitle = buttonLabel
    ? buttonLabel
    : isEdit
    ? "Edit Content"
    : "Add Content"
  const submitButtonText = buttonLabel
    ? buttonLabel
    : isEdit
    ? "Save Changes"
    : "Add Content"

  // Fixed checkbox data structure with numeric IDs
  const checkBoxData = [{ id: 1, name: "Published" }]

  const prevContents = Array.isArray(data?.contents)
    ? data.contents.filter(item => item?.id !== currentContent?.id)
    : []

  const vehicleId = data?.vehicle_stock_id || data?.id

  const { mutateAsync: createPublish, isPending: createLoading } =
    useCreateVehicleStockPublish({
      onSuccess: () => {
        if (onSuccess) onSuccess()
      },
    })

  const { mutateAsync: editPublish, isPending: editLoading } =
    useEditVehicleStockPublish({
      onSuccess: () => {
        if (onSuccess) onSuccess()
      },
    })

  const isSubmitting = createLoading || editLoading

  const toggle = useCallback(() => {
    setModal(prev => !prev)
  }, [])

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const payload = {
          vehicle_stock_id: data?.vehicle_stock_id,
          platform_id: data?.platform_id,
          contents: [
            ...prevContents,
            {
              id: currentContent?.id,
              url: values.url || null,
              remarks: values.remarks || "",
              is_published: values.is_published.includes(1),
            },
          ],
        }

        const action = isEdit
          ? editPublish({ id: data.id, ...payload })
          : createPublish(payload)

        const res = await action

        toast.success(
          res?.message ??
            `Vehicle stock content ${
              isEdit ? "updated" : "created"
            } successfully.`
        )
        resetForm()
        setModal(false)
      } catch (error) {
        console.error(error)
        toast.error("Failed to save content.")
      }
    },
    [isEdit, currentContent, createPublish, editPublish, prevContents, data]
  )

  const validationSchema = Yup.object({})

  // FIX: Provide proper default values to avoid undefined
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_stock_id: data?.vehicle_stock_id || "",
      platform_id: data?.platform_id || "",
      url: currentContent?.url || "", // Ensure empty string instead of undefined
      remarks: currentContent?.remarks || "", // Ensure empty string instead of undefined
      is_published: currentContent?.is_published ? [1] : [], // Use numeric ID
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  return (
    <div>
      <CanView
        access={[
          ACCESS.VEHICLE_CONTENT.ADD_VEHICLE_CONTENT,
          ACCESS.VEHICLE_CONTENT.EDIT_VEHICLE_CONTENT,
        ]}
      >
        <ButtonTooltip
          type={buttonType}
          onClick={toggle}
          is_label={isLabel}
          btnId={`vehicle-content-add-edit-btn-${
            currentContent?.id ?? "parent"
          }`}
          btnName={buttonName}
        />
      </CanView>
      <CanView
        access={[
          ACCESS.VEHICLE_CONTENT.ADD_VEHICLE_CONTENT,
          ACCESS.VEHICLE_CONTENT.EDIT_VEHICLE_CONTENT,
        ]}
      >
        <Modal isOpen={modal} toggle={toggle} centered>
          <ModalHeader toggle={toggle}>{modalTitle}</ModalHeader>
          <ModalBody>
            <Form
              onSubmit={e => {
                e.preventDefault()
                formik.handleSubmit()
              }}
            >
              <div className="mb-3">
                <Label htmlFor="url">Content URL</Label>
                <Input
                  name="url"
                  id="url"
                  type="text"
                  placeholder="Enter content URL"
                  onChange={formik.handleChange}
                  value={formik.values.url} // This will never be undefined now
                />
              </div>

              <div className="mb-3">
                <Label htmlFor="remarks">Remarks</Label>
                <Input
                  name="remarks"
                  id="remarks"
                  type="textarea"
                  placeholder="Add any remarks"
                  onChange={formik.handleChange}
                  value={formik.values.remarks} // This will never be undefined now
                />
              </div>

              <div className="form-check mb-3 ps-0">
                <ButtonCheckboxList
                  items={checkBoxData}
                  name="is_published"
                  selected={formik.values.is_published}
                  onChange={newSelected =>
                    formik.setFieldValue("is_published", newSelected)
                  }
                  loading={loading}
                  itemName="options"
                  showSelectionSummary={false}
                  isShowHeader={false}
                />
              </div>

              <div className="text-end">
                <Button color="success" type="submit" disabled={isSubmitting}>
                  {submitButtonText}
                  {isSubmitting && "..."}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </CanView>
    </div>
  )
}

export default VehicleStockContentAddEditModal
